"use strict";
/**
 * An event that can occur within a Device.
 *
 * Based on the following information:
 *
 * - {@link https://is.gd/gMIpZw Events Channel Specs}
 * - {@link https://is.gd/3WAgsv Event Data Specs}
 * - {@link https://is.gd/pwbvOo List of Known Events (Q2 2024)}
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.EventName = exports.Event = exports.events = exports.FilterLog = exports.RadioSurvey = exports.MilestoneAchieved = exports.CalibrationCompleted = exports.DailyPowerConsumed = exports.DailyWaterDispensed = exports.ActionCancelled = exports.CancellableEvent = exports.BottleWashed = exports.DrinkDispensed = exports.DispenseFailed = exports.DispensedEnhancement = exports.DispensedWater = exports.Session = exports.Id = exports.NAMESPACE = void 0;
const effect_1 = require("effect");
const date_1 = require("../../../date");
const survey_1 = require("../../../network/survey");
const Unit = __importStar(require("../../../unit"));
const volume_1 = require("../../../unit/volume");
const water_1 = require("../../water");
const _util_1 = require("../_util");
const session_1 = require("../session");
const definition_1 = require("./consumable/definition");
exports.NAMESPACE = 'Culligan.Device.Capability.Event';
exports.Id = effect_1.Schema.NonEmptyString.annotations({
    identifier: `${exports.NAMESPACE}.Id`,
});
exports.Session = effect_1.Schema.Union(session_1.Session, _util_1.EmptyObject).annotations({
    identifier: `${exports.NAMESPACE}.Session`,
});
exports.DispensedWater = effect_1.Schema.Struct({
    type: water_1.Type,
    volume: Unit.FromNumber(Unit.Volume.Milliliter),
}).annotations({
    identifier: `${exports.NAMESPACE}.DispensedWater`,
});
const MilliliterFromNumericLiter = effect_1.Schema.transform(Unit.FromNumber(Unit.Volume.Liter), Unit.Volume.Milliliter, {
    decode: Unit.Volume.convert('Milliliter'),
    encode: Unit.Volume.convert('Liter'),
}).annotations({
    identifier: `${exports.NAMESPACE}.MilliliterFromNumericLiter`,
});
exports.DispensedEnhancement = effect_1.Schema.Struct({
    subsetId: definition_1.Id,
    amount: MilliliterFromNumericLiter,
}).annotations({
    identifier: `${exports.NAMESPACE}.DispensedEnhancement`,
});
class DispenseFailed extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.DispenseFailed`)('dispenseFailed', {
    params: effect_1.Schema.Struct({
        session: exports.Session,
        water: exports.DispensedWater,
        enhancements: effect_1.Schema.Array(exports.DispensedEnhancement),
        errors: effect_1.Schema.String,
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.DispenseFailed`,
    description: 'When a user attempts to dispense a drink, but it fails',
}) {
    static example = new DispenseFailed({
        time: new Date(1714041159978),
        params: {
            errors: 'No flow detected (tOn=1842321s)',
            water: { type: 1, volume: Unit.Volume.Milliliter.make({ amount: 0 }) },
            enhancements: [
                { subsetId: 'tropical', amount: Unit.Volume.Milliliter.make({ amount: 1_000 }) },
            ],
            session: {
                id: '6a6b80',
                type: 0,
                startTime: new Date('2024-03-06T09:39:51.837Z'),
                endTime: new Date('2024-03-06T09:40:29.725Z'),
                user: null,
            },
        },
    });
}
exports.DispenseFailed = DispenseFailed;
class DrinkDispensed extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.DrinkDispensed`)('drinkDispensed', {
    params: effect_1.Schema.Struct({
        session: exports.Session,
        water: exports.DispensedWater,
        enhancements: effect_1.Schema.Array(exports.DispensedEnhancement),
        flowRate: effect_1.Schema.optionalWith(Unit.FromNumber(Unit.FlowRate.MillilitersPerSecond), {
            exact: true,
        }).annotations({
            description: 'Average flow rate over the whole dispense. Absent when the unit cannot measure it.',
        }),
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.DrinkDispensed`,
    description: 'When a user dispenses a drink, for plain and enhanced water',
}) {
    static example = new DrinkDispensed({
        time: new Date(1714041159978),
        params: {
            water: { type: 1, volume: Unit.Volume.Milliliter.make({ amount: 5 }) },
            enhancements: [
                { subsetId: 'tropical', amount: Unit.Volume.Milliliter.make({ amount: 1_000 }) },
            ],
            flowRate: Unit.FlowRate.MillilitersPerSecond.make({ amount: 22.5 }),
            session: {
                id: '6a6b80',
                type: 0,
                startTime: new Date('2024-03-06T09:39:51.837Z'),
                endTime: new Date('2024-03-06T09:40:29.725Z'),
                user: null,
            },
        },
    });
}
exports.DrinkDispensed = DrinkDispensed;
DrinkDispensed;
class BottleWashed extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.BottleWashed`)('bottleWashed', {
    params: effect_1.Schema.Struct({
        session: exports.Session,
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.BottleWashed`,
    description: 'When a bottle is washed successfully',
}) {
    static example = new BottleWashed({
        time: new Date(1714041159978),
        params: {
            session: {
                id: '6a6b80',
                type: 0,
                startTime: new Date('2024-03-06T09:39:51.837Z'),
                endTime: new Date('2024-03-06T09:40:29.725Z'),
                user: { id: 'bob' },
            },
        },
    });
}
exports.BottleWashed = BottleWashed;
BottleWashed;
exports.CancellableEvent = effect_1.Schema.Union(DrinkDispensed, BottleWashed).annotations({
    identifier: `${exports.NAMESPACE}.CancellableEvent`,
    description: 'A union of events that can be cancelled by the user after initiation',
});
class ActionCancelled extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.ActionCancelled`)('actionCancelled', {
    params: effect_1.Schema.Struct({
        event: effect_1.Schema.Literal(...exports.CancellableEvent.members.map(e => e._tag)),
        session: exports.Session,
        reason: effect_1.Schema.String,
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.ActionCancelled`,
    description: 'An in-progress event was explicitly canceled by the user',
}) {
    static example = new ActionCancelled({
        time: new Date(1714041159978),
        params: {
            event: 'drinkDispensed',
            reason: 'user pressed cancel button',
            session: {
                id: '6a6b80',
                type: 0,
                startTime: new Date('2024-03-06T09:39:51.837Z'),
                endTime: new Date('2024-03-06T09:40:29.725Z'),
                user: { id: 'bob' },
            },
        },
    });
}
exports.ActionCancelled = ActionCancelled;
ActionCancelled;
class DailyWaterDispensed extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.DailyWaterDispensed`)('dayWc', {
    params: effect_1.Schema.Struct({
        dayWc: Unit.FromNumber(Unit.Volume.Liter),
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.DailyWaterDispensed`,
    description: 'Liters of water dispensed by the unit in the last 24 hours',
}) {
    static example = new DailyWaterDispensed({
        time: new Date(1714041159978),
        params: {
            dayWc: Unit.Volume.Liter.make({ amount: 112 }),
        },
    });
}
exports.DailyWaterDispensed = DailyWaterDispensed;
DailyWaterDispensed;
class DailyPowerConsumed extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.DailyPowerConsumed`)('dayPc', {
    params: effect_1.Schema.Struct({
        dayPc: Unit.FromNumber(Unit.Energy.KilowattHour),
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.DailyPowerConsumed`,
    description: 'Kilowatt-hours of energy consumed by the unit in the last 24 hours',
}) {
    static example = new DailyPowerConsumed({
        time: new Date(1714041159978),
        params: {
            dayPc: Unit.Energy.KilowattHour.make({ amount: 12312.22 }),
        },
    });
}
exports.DailyPowerConsumed = DailyPowerConsumed;
DailyPowerConsumed;
class CalibrationCompleted extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.CalibrationCompleted`)('calibrationCompleted', {
    params: effect_1.Schema.Struct({}),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.CalibrationCompleted`,
    description: 'Calibration of the unit has been completed successfully',
}) {
    static example = new CalibrationCompleted({
        time: new Date(1714041159978),
        params: {},
    });
}
exports.CalibrationCompleted = CalibrationCompleted;
CalibrationCompleted;
class MilestoneAchieved extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.MilestoneAchieved`)('milestoneAchieved', {
    params: effect_1.Schema.Struct({
        liters: volume_1.Liter.fields.amount,
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.MilestoneAchieved`,
    description: 'The unit has dispensed a total of 50 liters of water',
}) {
    static example = new MilestoneAchieved({
        time: new Date(1714041159978),
        params: {
            liters: 50,
        },
    });
}
exports.MilestoneAchieved = MilestoneAchieved;
MilestoneAchieved;
class RadioSurvey extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.RadioSurvey`)('radioSurvey', {
    params: effect_1.Schema.Struct({
        surveys: effect_1.Schema.NonEmptyArray(survey_1.Survey),
    }),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.RadioSurvey`,
    description: 'One or more radio surveys performed by the unit',
}) {
    static example = new RadioSurvey({
        time: new Date(1714041159978),
        params: {
            surveys: [
                {
                    technology: 'WI-FI',
                    format: 'CSV',
                    data: [
                        {
                            bssid: '55:55:55:55:55:55',
                            channel: 5,
                            rssi: -50,
                        },
                    ],
                },
            ],
        },
    });
}
exports.RadioSurvey = RadioSurvey;
RadioSurvey;
class FilterLog extends effect_1.Schema.TaggedClass(`${exports.NAMESPACE}.FilterLog`)('filterLog', {
    params: effect_1.Schema.Struct({
        liters: Unit.FromNumber(Unit.Volume.Liter),
        boilingPercent: effect_1.Schema.Int.pipe(effect_1.Schema.between(0, 100)),
        chilledPercent: effect_1.Schema.Int.pipe(effect_1.Schema.between(0, 100)),
        sparklingPercent: effect_1.Schema.Int.pipe(effect_1.Schema.between(0, 100)),
        ambientPercent: effect_1.Schema.Int.pipe(effect_1.Schema.between(0, 100)),
    }).pipe(effect_1.Schema.filter(params => params.boilingPercent +
        params.chilledPercent +
        params.sparklingPercent +
        params.ambientPercent ===
        100, {
        message: () => `The sum of boilingPercent, chilledPercent, sparklingPercent, and ambientPercent must equal 100`,
    })),
    time: date_1.Timestamp,
}, {
    identifier: `${exports.NAMESPACE}.FilterLog`,
    description: 'Filter usage history log entry when filter counter is reset',
}) {
    static example = new FilterLog({
        time: new Date(1770862820000),
        params: {
            liters: Unit.Volume.Liter.make({ amount: 4012 }),
            boilingPercent: 40,
            chilledPercent: 25,
            sparklingPercent: 35,
            ambientPercent: 0,
        },
    });
}
exports.FilterLog = FilterLog;
FilterLog;
exports.events = {
    DispenseFailed,
    DrinkDispensed,
    BottleWashed,
    ActionCancelled,
    DailyPowerConsumed,
    DailyWaterDispensed,
    CalibrationCompleted,
    MilestoneAchieved,
    RadioSurvey,
    FilterLog,
};
exports.Event = effect_1.Schema.Union(...effect_1.Record.values(exports.events)).annotations({
    identifier: `${exports.NAMESPACE}.Event`,
});
exports.EventName = effect_1.Schema.Literal(...effect_1.Record.values(exports.events).map(e => e._tag)).annotations({
    identifier: `${exports.NAMESPACE}.EventName`,
});
//# sourceMappingURL=event.js.map